import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useFlashMessage } from './FlashMessage';
import { LoadingSpinner } from './ui';
import LeaveRequestInlineCalendar from './LeaveRequestInlineCalendar';
import { AuthContext } from '../context/AuthContext';
import { uploadSickNoteComplete, listSickNoteDocuments } from '../services/docuwareService';
import leaveService from '../services/leaveService';
import { getErrorMessage } from '../utils/errorMessages';
import './LeaveRequestModal.css';

function LeaveRequestModal({ isOpen, onClose, onRequestSubmitted, leaveBalance, existingLeaveRequests = [], prefilledStartDate = null }) {
  const { showSuccess, showError } = useFlashMessage();
  const { user } = useContext(AuthContext);
  
  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Local error state for validation (doesn't trigger context re-render)
  const [localError, setLocalError] = useState('');
  const [showLocalError, setShowLocalError] = useState(false);
  const [exitAnimation, setExitAnimation] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    leave_type: null,
    start_date: '',
    end_date: '',
    date_range: '',
    start_day_type: 'full',
    end_day_type: 'full',
    reason: ''
  });
  
  // Sick note state
  const [sickNoteFile, setSickNoteFile] = useState(null);
  const [sickNoteUploading, setSickNoteUploading] = useState(false);
  const [sickNoteUploaded, setSickNoteUploaded] = useState(false);
  const [sickNoteInfo, setSickNoteInfo] = useState(null);
  const [sickNoteMode, setSickNoteMode] = useState('upload'); // 'upload' or 'select'
  const [availableSickNotes, setAvailableSickNotes] = useState([]);
  const [selectedExistingSickNote, setSelectedExistingSickNote] = useState(null);
  const [loadingSickNotes, setLoadingSickNotes] = useState(false);
  
  // Calculated values
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [affectedYears, setAffectedYears] = useState([]);
  const [spansMultipleYears, setSpansMultipleYears] = useState(false);
  
  // Consecutive sick days state (includes adjacent requests)
  const [consecutiveSickDays, setConsecutiveSickDays] = useState(0);
  const [requiresSickNote, setRequiresSickNote] = useState(false);

  // Calculate blocked dates from existing leave requests (pending or approved only)
  const blockedDates = useMemo(() => {
    const blocked = new Set();
    
    // Filter for pending and approved leave only
    const activeLeave = existingLeaveRequests.filter(
      request => request.status === 'pending' || request.status === 'approved'
    );
    
    // For each active leave request, add all dates in the range to the blocked set
    activeLeave.forEach(request => {
      if (!request.start_date || !request.end_date) return;
      
      // Parse date strings directly to avoid timezone issues
      const [startYear, startMonth, startDay] = request.start_date.split('-').map(Number);
      const [endYear, endMonth, endDay] = request.end_date.split('-').map(Number);
      
      // Create dates at local midnight to avoid timezone offset issues
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);
      const current = new Date(start);
      
      // Add all dates in the range (including weekends for simplicity)
      while (current <= end) {
        // Format as YYYY-MM-DD
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        blocked.add(dateStr);
        current.setDate(current.getDate() + 1);
      }
    });
    
    return blocked;
  }, [existingLeaveRequests]);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/leave/types/');
      setLeaveTypes(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching leave types:', error);
      showError('Failed to load leave types');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchAffectedYears = useCallback(async () => {
    if (!formData.start_date || !formData.end_date) {
      setCalculatedDays(0);
      setAffectedYears([]);
      setSpansMultipleYears(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_day_type: formData.start_day_type,
        end_day_type: formData.end_day_type
      });

      const response = await axiosInstance.get(`/api/leave/affected-years/?${params}`);
      const { affected_years, total_days, spans_multiple_years } = response.data;

      setAffectedYears(affected_years);
      setCalculatedDays(total_days);
      setSpansMultipleYears(spans_multiple_years);
    } catch (error) {
      console.error('Error fetching affected years:', error);
      // Fallback to client-side calculation
      calculateTotalDaysClientSide();
    }
  }, [formData.start_date, formData.end_date, formData.start_day_type, formData.end_day_type]);

  const calculateTotalDaysClientSide = useCallback(() => {
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    
    if (startDate > endDate) {
      setCalculatedDays(0);
      return;
    }

    let totalDays = 0;
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      // Skip weekends (assuming 5-day work week)
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        if (currentDate.getTime() === startDate.getTime() && formData.start_day_type !== 'full') {
          totalDays += 0.5;
        } else if (currentDate.getTime() === endDate.getTime() && formData.end_day_type !== 'full') {
          totalDays += 0.5;
        } else {
          totalDays += 1;
        }
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setCalculatedDays(totalDays);
    setSpansMultipleYears(false);
    setAffectedYears([]);
  }, [formData.start_date, formData.end_date, formData.start_day_type, formData.end_day_type]);

  // Fetch leave types on component mount
  useEffect(() => {
    if (isOpen) {
      fetchLeaveTypes();
      resetForm();
    }
  }, [isOpen, fetchLeaveTypes]);

  // Calculate days and fetch affected years when dates change
  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      fetchAffectedYears();
    } else {
      setCalculatedDays(0);
      setAffectedYears([]);
      setSpansMultipleYears(false);
    }
  }, [formData.start_date, formData.end_date, formData.start_day_type, formData.end_day_type, fetchAffectedYears]);

  // Calculate consecutive sick days when dates change and leave type is Sick Leave
  useEffect(() => {
    const calculateConsecutiveDays = async () => {
      const selectedLeaveType = leaveTypes.find(type => type.id === formData.leave_type);
      
      if (selectedLeaveType?.name === 'Sick Leave' && formData.start_date && formData.end_date) {
        try {
          const result = await leaveService.calculateConsecutiveSickDays(
            formData.start_date,
            formData.end_date
          );
          setConsecutiveSickDays(result.consecutive_days);
          setRequiresSickNote(result.requires_sick_note);
          
          console.log(`Consecutive sick days: ${result.consecutive_days}, Requires sick note: ${result.requires_sick_note}`);
        } catch (error) {
          console.error('Error calculating consecutive sick days:', error);
          // Fallback to using just the current request days
          setConsecutiveSickDays(calculatedDays);
          setRequiresSickNote(calculatedDays >= 3);
        }
      } else {
        // Reset if not sick leave
        setConsecutiveSickDays(0);
        setRequiresSickNote(false);
      }
    };
    
    calculateConsecutiveDays();
  }, [formData.leave_type, formData.start_date, formData.end_date, leaveTypes, calculatedDays]);

  // Fetch available sick notes when sick note is required
  useEffect(() => {
    const fetchSickNotes = async () => {
      if (requiresSickNote && user?.id) {
        setLoadingSickNotes(true);
        try {
          const response = await listSickNoteDocuments(user.id);
          if (response && response.Items) {
            setAvailableSickNotes(response.Items);
          }
        } catch (error) {
          console.error('Error fetching sick notes:', error);
          setAvailableSickNotes([]);
        } finally {
          setLoadingSickNotes(false);
        }
      } else {
        setAvailableSickNotes([]);
      }
    };
    
    fetchSickNotes();
  }, [requiresSickNote, user]);

  const resetForm = () => {
    setCurrentStep(1);
    setLocalError('');
    setShowLocalError(false);
    setExitAnimation(false);
    
    // Format prefilled start date if provided
    let formattedStartDate = '';
    if (prefilledStartDate) {
      const year = prefilledStartDate.getFullYear();
      const month = String(prefilledStartDate.getMonth() + 1).padStart(2, '0');
      const day = String(prefilledStartDate.getDate()).padStart(2, '0');
      formattedStartDate = `${year}-${month}-${day}`;
    }
    
    setFormData({
      leave_type: null,
      start_date: formattedStartDate,
      end_date: '',
      date_range: '',
      start_day_type: 'full',
      end_day_type: 'full',
      reason: ''
    });
    setCalculatedDays(0);
    setAffectedYears([]);
    setSpansMultipleYears(false);
    
    // Clear sick note state
    setSickNoteFile(null);
    setSickNoteInfo(null);
    setSickNoteUploading(false);
    setSickNoteUploaded(false);
    setSickNoteMode('upload');
    setAvailableSickNotes([]);
    setSelectedExistingSickNote(null);
    setLoadingSickNotes(false);
    
    // Reset file input
    const fileInput = document.getElementById('sick-note-file-input');
    if (fileInput) fileInput.value = '';
  };

  // Show local error message (slides in from right like flash messages)
  const showLocalErrorMessage = (message) => {
    setLocalError(message);
    setShowLocalError(true);
    setExitAnimation(false);
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      closeLocalError();
    }, 5000);
  };

  // Close local error message with slide-out animation
  const closeLocalError = () => {
    setExitAnimation(true);
    
    // Wait for exit animation to complete before hiding
    setTimeout(() => {
      setShowLocalError(false);
      setExitAnimation(false);
    }, 300); // Duration of exit animation
  };

  // Handler for inline calendar date selection
  const handleInlineCalendarSelect = (start, end) => {
    if (start && end) {
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const formattedStart = formatDate(start);
      const formattedEnd = formatDate(end);
      const dateRange = `${formattedStart} to ${formattedEnd}`;
      
      setFormData({
        ...formData,
        date_range: dateRange,
        start_date: formattedStart,
        end_date: formattedEnd
      });
    } else if (start) {
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const formattedStart = formatDate(start);
      
      setFormData({
        ...formData,
        date_range: formattedStart,
        start_date: formattedStart,
        end_date: ''
      });
    } else {
      // Clear dates
      setFormData({
        ...formData,
        date_range: '',
        start_date: '',
        end_date: ''
      });
    }
  };

  // Sick note file handling
  const handleSickNoteFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      showError('Invalid file type. Please upload a PDF, JPG, or PNG file.');
      return;
    }
    
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      showError('File size exceeds 10MB. Please upload a smaller file.');
      return;
    }
    
    setSickNoteFile(file);
    setSickNoteInfo({
      name: file.name,
      size: file.size,
      uploadDate: new Date().toISOString()
    });
  };

  const removeSickNoteFile = () => {
    setSickNoteFile(null);
    setSickNoteInfo(null);
    // Reset file input
    const fileInput = document.getElementById('sick-note-file-input');
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Check if selected date range overlaps with existing leave (including encapsulation)
  const checkDateRangeOverlap = (startDateStr, endDateStr) => {
    // Parse the selected dates
    const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number);
    const selectedStart = new Date(startYear, startMonth - 1, startDay);
    const selectedEnd = new Date(endYear, endMonth - 1, endDay);
    
    // Filter for pending and approved leave only
    const activeLeave = existingLeaveRequests.filter(
      request => request.status === 'pending' || request.status === 'approved'
    );
    
    // Check each existing leave request for overlap
    for (const request of activeLeave) {
      if (!request.start_date || !request.end_date) continue;
      
      // Parse existing leave dates
      const [existingStartYear, existingStartMonth, existingStartDay] = request.start_date.split('-').map(Number);
      const [existingEndYear, existingEndMonth, existingEndDay] = request.end_date.split('-').map(Number);
      const existingStart = new Date(existingStartYear, existingStartMonth - 1, existingStartDay);
      const existingEnd = new Date(existingEndYear, existingEndMonth - 1, existingEndDay);
      
      // Check for any overlap scenarios:
      // 1. Selected range starts during existing leave
      // 2. Selected range ends during existing leave
      // 3. Selected range completely encapsulates existing leave
      // 4. Existing leave completely encapsulates selected range
      const overlaps = (
        (selectedStart <= existingEnd && selectedEnd >= existingStart) // Any overlap
      );
      
      if (overlaps) {
        return true;
      }
    }
    
    return false;
  };

  const validateStep = (step) => {
    let isValid = true;

    switch (step) {
      case 1:
        if (!formData.leave_type) {
          showLocalErrorMessage('Please select a leave type');
          isValid = false;
        }
        break;
      case 2:
        if (!formData.start_date) {
          showLocalErrorMessage('Please select a start date');
          isValid = false;
        } else if (!formData.end_date) {
          showLocalErrorMessage('Please select an end date');
          isValid = false;
        } else if (formData.start_date && formData.end_date) {
          const startDate = new Date(formData.start_date);
          const endDate = new Date(formData.end_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (startDate < today) {
            showLocalErrorMessage('Cannot request leave for past dates');
            isValid = false;
          } else if (startDate > endDate) {
            showLocalErrorMessage('End date cannot be before start date');
            isValid = false;
          } else {
            // Check if selected date range overlaps with existing leave
            const overlapDetected = checkDateRangeOverlap(formData.start_date, formData.end_date);
            if (overlapDetected) {
              showLocalErrorMessage(
                'Selected date range overlaps with existing leave. Please select different dates.'
              );
              isValid = false;
            }
            // Only check balance for leave types that have balance limits
            const selectedLeaveType = leaveTypes.find(type => type.id === formData.leave_type);
            if (selectedLeaveType && selectedLeaveType.balance_category !== 'unlimited') {
              // For multi-year requests, check each year's balance
              if (spansMultipleYears && affectedYears.length > 0) {
                for (const yearData of affectedYears) {
                  const yearBalance = yearData.balance;
                  const yearDays = yearData.days;
                  
                  if (selectedLeaveType.balance_category === 'annual') {
                    if (yearDays > (yearBalance?.available_days || 0)) {
                      showLocalErrorMessage(
                        `Insufficient annual leave balance for year ${yearData.year}. ` +
                        `Available: ${yearBalance?.available_days || 0} days, Required: ${yearDays} days`
                      );
                      isValid = false;
                      break;
                    }
                  } else if (selectedLeaveType.balance_category === 'personal_bereavement') {
                    if (yearDays > (yearBalance?.personal_bereavement_available || 0)) {
                      showLocalErrorMessage(
                        `Insufficient personal/bereavement balance for year ${yearData.year}. ` +
                        `Available: ${yearBalance?.personal_bereavement_available || 0} days, Required: ${yearDays} days`
                      );
                      isValid = false;
                      break;
                    }
                  }
                }
              } else {
                // Single year request - use current leave balance
                if (selectedLeaveType.balance_category === 'annual') {
                  if (calculatedDays > (leaveBalance?.available_days || 0)) {
                    showLocalErrorMessage(`Insufficient annual leave balance. Available: ${leaveBalance?.available_days || 0} days`);
                    isValid = false;
                  }
                } else if (selectedLeaveType.balance_category === 'personal_bereavement') {
                  if (calculatedDays > (leaveBalance?.personal_bereavement_available || 0)) {
                    showLocalErrorMessage(`Insufficient personal/bereavement balance. Available: ${leaveBalance?.personal_bereavement_available || 0} days`);
                    isValid = false;
                  }
                }
              }
            }
          }
        }
        break;
      case 3:
        const selectedLeaveType = leaveTypes.find(type => type.id === formData.leave_type);
        if (selectedLeaveType?.requires_reason && !formData.reason.trim()) {
          showLocalErrorMessage(`${selectedLeaveType.name} requires a reason`);
          isValid = false;
        }
        // Check if sick note is required for consecutive sick leave >= 3 days
        if (requiresSickNote && !sickNoteFile && !selectedExistingSickNote) {
          showLocalErrorMessage(
            consecutiveSickDays > calculatedDays 
              ? `Sick note required: ${consecutiveSickDays} consecutive days (with prior leave)`
              : 'Sick note required for 3 or more consecutive days'
          );
          isValid = false;
        }
        break;
      default:
        // No validation needed for other steps
        break;
    }

    return isValid;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    try {
      setSubmitting(true);
      
      const requestData = {
        leave_type: formData.leave_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_day_type: formData.start_day_type,
        end_day_type: formData.end_day_type,
        reason: formData.reason.trim()
      };

      // Step 1: Create leave request
      const response = await axiosInstance.post('/api/leave/requests/', requestData);
      const leaveRequestId = response.data.id;
      
      // Step 2: Handle sick note (upload new or link existing)
      if (requiresSickNote) {
        if (sickNoteFile) {
          // Upload new sick note
          try {
            setSickNoteUploading(true);
            
            // Use leave request ID as sick note ID (must be integer for DocuWare)
            const sickNoteId = leaveRequestId;
            
            // Upload to DocuWare (creates record + uploads file)
            const uploadResult = await uploadSickNoteComplete(
              user.id,
              sickNoteId,
              sickNoteFile,
              formData.reason
            );
            
            // Update leave request with sick note details
            await axiosInstance.patch(`/api/leave/requests/${leaveRequestId}/`, {
              sick_note_id: sickNoteId.toString(),
              sick_note_docuware_document_id: uploadResult.documentId,
              sick_note_name: sickNoteFile.name,
              sick_note_description: formData.reason,
              sick_note_uploaded_at: new Date().toISOString()
            });
            
            setSickNoteUploaded(true);
            console.log('Sick note uploaded successfully');
          } catch (uploadError) {
            console.error('Error uploading sick note:', uploadError);
            // Note: Leave request was already created, just log the error
            // The user can upload the sick note later if needed
            showError('Leave request created, but failed to upload sick note. Please contact support.');
          } finally {
            setSickNoteUploading(false);
          }
        } else if (selectedExistingSickNote) {
          // Link existing sick note
          try {
            setSickNoteUploading(true);
            
            // Debug: Log what we're searching for
            console.log('Searching for sick note with ID:', selectedExistingSickNote);
            console.log('Available sick notes array:', availableSickNotes);
            console.log('Available sick notes IDs:', availableSickNotes.map(note => ({ Id: note.Id, type: typeof note.Id })));
            console.log('Selected ID type:', typeof selectedExistingSickNote);
            
            // Get the sick note details from the available sick notes
            const selectedNote = availableSickNotes.find(note => {
              console.log(`Comparing note.Id (${note.Id}, ${typeof note.Id}) === selectedExistingSickNote (${selectedExistingSickNote}, ${typeof selectedExistingSickNote})`);
              return note.Id == selectedExistingSickNote; // Use == for loose comparison
            });
            
            // Debug: Log the entire document structure to see what fields are available
            console.log('Selected sick note document structure:', selectedNote);
            console.log('Available Fields:', selectedNote?.Fields);
            
            // Try multiple sources to get the actual filename (never use generic "Sick Note")
            let fileName = selectedNote?.Fields?.find(f => f.FieldName === 'SICK_NOTE_NAME')?.Item;
            console.log('SICK_NOTE_NAME field value:', fileName);
            
            if (!fileName) {
              // Try DocuWare's stored filename
              fileName = selectedNote?.Fields?.find(f => f.FieldName === 'DWSTOREDFILENAME')?.Item;
              console.log('DWSTOREDFILENAME field value:', fileName);
            }
            if (!fileName) {
              // Try other potential filename fields
              fileName = selectedNote?.Fields?.find(f => f.FieldName === 'FILENAME')?.Item;
              console.log('FILENAME field value:', fileName);
            }
            if (!fileName) {
              // Try ContentFileName from the document object
              fileName = selectedNote?.ContentFileName;
              console.log('ContentFileName value:', fileName);
            }
            if (!fileName) {
              // Try FileSize field to confirm document has content
              const hasFile = selectedNote?.Fields?.find(f => f.FieldName === 'DWDOCSIZE')?.Item;
              console.log('Document has file (DWDOCSIZE):', hasFile);
              
              // Last resort - use document ID
              fileName = `sick_note_${selectedExistingSickNote}.pdf`;
              console.log('Using fallback filename:', fileName);
            }
            
            console.log('Final filename selected:', fileName);
            
            // Update leave request with existing sick note details
            await axiosInstance.patch(`/api/leave/requests/${leaveRequestId}/`, {
              sick_note_id: leaveRequestId.toString(), // Use leave request ID
              sick_note_docuware_document_id: selectedExistingSickNote,
              sick_note_name: fileName,
              sick_note_description: formData.reason,
              sick_note_uploaded_at: new Date().toISOString()
            });
            
            setSickNoteUploaded(true);
            console.log('Existing sick note linked successfully');
          } catch (linkError) {
            console.error('Error linking existing sick note:', linkError);
            showError('Leave request created, but failed to link sick note. Please contact support.');
          } finally {
            setSickNoteUploading(false);
          }
        }
      }
      
      showSuccess('Leave request submitted successfully!');
      onRequestSubmitted();
      onClose();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      showError(getErrorMessage(error, 'Leave request could not be submitted. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedLeaveType = () => {
    return leaveTypes.find(type => type.id === formData.leave_type);
  };

  const getBalanceCheckMessage = () => {
    if (!leaveBalance || calculatedDays === 0) return '';
    
    const selectedLeaveType = getSelectedLeaveType();
    if (!selectedLeaveType) return '';
    
    const category = selectedLeaveType.balance_category;
    
    // Unlimited leave types - no balance check
    if (category === 'unlimited') {
      return 'Usage will be tracked (no limit)';
    }
    
    // Annual leave
    if (category === 'annual') {
      const remainingDays = leaveBalance.available_days - calculatedDays;
      if (remainingDays < 0) {
        return `Insufficient annual leave balance (${formatDays(Math.abs(remainingDays))} days over limit)`;
      } else if (remainingDays === 0) {
        return 'This will use all your remaining annual leave';
      } else {
        return `${formatDays(remainingDays)} annual leave days will remain`;
      }
    }
    
    // Personal/Bereavement leave
    if (category === 'personal_bereavement') {
      const remainingDays = leaveBalance.personal_bereavement_available - calculatedDays;
      if (remainingDays < 0) {
        return `Insufficient personal/bereavement balance (${formatDays(Math.abs(remainingDays))} days over limit)`;
      } else if (remainingDays === 0) {
        return 'This will use all your remaining personal/bereavement leave';
      } else {
        return `${formatDays(remainingDays)} personal/bereavement days will remain`;
      }
    }
    
    return '';
  };

  const getBalanceCheckClass = () => {
    if (!leaveBalance || calculatedDays === 0) return '';
    
    const selectedLeaveType = getSelectedLeaveType();
    if (!selectedLeaveType) return '';
    
    const category = selectedLeaveType.balance_category;
    
    // Unlimited leave types - always OK
    if (category === 'unlimited') {
      return 'balance-unlimited';
    }
    
    // Annual leave
    if (category === 'annual') {
      const remainingDays = leaveBalance.available_days - calculatedDays;
      if (remainingDays < 0) return 'balance-insufficient';
      if (remainingDays === 0) return 'balance-warning';
      return 'balance-ok';
    }
    
    // Personal/Bereavement leave
    if (category === 'personal_bereavement') {
      const remainingDays = leaveBalance.personal_bereavement_available - calculatedDays;
      if (remainingDays < 0) return 'balance-insufficient';
      if (remainingDays === 0) return 'balance-warning';
      return 'balance-ok';
    }
    
    return '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const formatDays = (days) => {
    const num = parseFloat(days);
    return num % 1 === 0 ? Math.floor(num) : num.toFixed(1);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Local Flash Message - Slides in from right, doesn't cause modal reload */}
      {showLocalError && (
        <div className="leave-request-flash-message-container">
          <div className={`leave-request-flash-message leave-request-flash-error ${exitAnimation ? 'leave-request-flash-exit' : ''}`}>
            <div className="leave-request-flash-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div className="leave-request-flash-content">
              <p>{localError}</p>
            </div>
            <button 
              className="leave-request-flash-close" 
              onClick={closeLocalError}
              aria-label="Close message"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="leave-request-modal-overlay-unique" onClick={onClose}>
        <div className="leave-request-modal-unique" onClick={(e) => e.stopPropagation()}>
        <div className="leave-request-modal-header">
          <div className="leave-request-header-content">
            <h3 className="leave-request-modal-title">Request Leave</h3>
            <div className="leave-request-step-indicator">
              <span className="leave-request-step-text">Step {currentStep} of 3</span>
              <div className="leave-request-step-progress">
                <div 
                  className="leave-request-step-progress-bar" 
                  style={{ width: `${(currentStep / 3) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
          <button className="leave-request-close-button" onClick={onClose}>×</button>
        </div>

        <div className="leave-request-modal-body">
          {loading ? (
            <LoadingSpinner message="Loading leave types..." />
          ) : (
            <>
              {/* Step 1: Leave Type Selection */}
              {currentStep === 1 && (
                <div className="leave-request-form-step">
                  <h4 className="leave-request-step-title">Select Leave Type</h4>
                  <div className="leave-request-leave-type-grid">
                    {leaveTypes.map(leaveType => (
                      <div
                        key={leaveType.id}
                        className={`leave-request-leave-type-card ${formData.leave_type === leaveType.id ? 'leave-request-selected' : ''}`}
                        onClick={() => setFormData({...formData, leave_type: leaveType.id})}
                      >
                        <div 
                          className="leave-request-type-color" 
                          style={{backgroundColor: leaveType.color}}
                        ></div>
                        <div className="leave-request-type-name">{leaveType.name}</div>
                        <div className="leave-request-type-description">{leaveType.description}</div>
                        {leaveType.requires_reason && (
                          <div className="leave-request-type-requirement">Reason required</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Date Selection */}
              {currentStep === 2 && (
                <div className="leave-request-form-step">
                  <div className="leave-request-date-selection">
                    {/* Inline Calendar - Always Visible */}
                    <LeaveRequestInlineCalendar
                      startDate={formData.start_date ? new Date(formData.start_date) : null}
                      endDate={formData.end_date ? new Date(formData.end_date) : null}
                      onDateSelect={handleInlineCalendarSelect}
                      minDate={new Date()}
                      blockedDates={blockedDates}
                    />

                    {/* Day Type Selections - Show only if partial days allowed */}
                    {getSelectedLeaveType()?.allows_partial_days && formData.start_date && formData.end_date && (
                      <div className="leave-request-day-types">
                        <div className="leave-request-input-group">
                          <label className="leave-request-label">Start Day Type</label>
                          <select
                            className="leave-request-select"
                            value={formData.start_day_type}
                            onChange={(e) => setFormData({...formData, start_day_type: e.target.value})}
                          >
                            <option value="full">Full Day</option>
                            <option value="morning">Morning Half</option>
                            <option value="afternoon">Afternoon Half</option>
                          </select>
                        </div>
                        <div className="leave-request-input-group">
                          <label className="leave-request-label">End Day Type</label>
                          <select
                            className="leave-request-select"
                            value={formData.end_day_type}
                            onChange={(e) => setFormData({...formData, end_day_type: e.target.value})}
                          >
                            <option value="full">Full Day</option>
                            <option value="morning">Morning Half</option>
                            <option value="afternoon">Afternoon Half</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Date and calculation display */}
                    {(formData.start_date || formData.end_date) && (
                      <div className="leave-request-calculation-display">
                        {formData.start_date && (
                          <div className="leave-request-calculation-row">
                            <span className="leave-request-calc-label">Start Date:</span>
                            <span className="leave-request-calc-value">{formatDate(formData.start_date)}</span>
                          </div>
                        )}
                        {formData.end_date && (
                          <div className="leave-request-calculation-row">
                            <span className="leave-request-calc-label">End Date:</span>
                            <span className="leave-request-calc-value">{formatDate(formData.end_date)}</span>
                          </div>
                        )}
                        {formData.start_date && formData.end_date && (
                          <>
                            <div className="leave-request-calculation-row">
                              <span className="leave-request-calc-label">Total Days:</span>
                              <span className="leave-request-calc-value">{formatDays(calculatedDays)} days</span>
                            </div>
                            
                            {/* Multi-year breakdown */}
                            {spansMultipleYears && affectedYears.length > 0 && (
                              <div className="leave-request-year-breakdown">
                                <div className="leave-request-year-breakdown-header">
                                  <i className="fas fa-calendar-alt" style={{marginRight: '8px'}}></i>
                                  Multi-Year Leave Breakdown
                                </div>
                                {affectedYears.map((yearData) => {
                                  const yearBalance = yearData.balance;
                                  const category = getSelectedLeaveType()?.balance_category;
                                  const available = category === 'personal_bereavement'
                                    ? yearBalance?.personal_bereavement_available
                                    : yearBalance?.available_days;
                                  
                                  return (
                                    <div key={yearData.year} className="leave-request-year-item">
                                      <div className="leave-request-year-label">
                                        Year {yearData.year}:
                                      </div>
                                      <div className="leave-request-year-details">
                                        <span className="leave-request-year-days">
                                          {formatDays(yearData.days)} days
                                        </span>
                                        {category !== 'unlimited' && (
                                          <span className="leave-request-year-balance">
                                            (Balance: {formatDays(available || 0)} days)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Single year balance display */}
                            {!spansMultipleYears && getSelectedLeaveType()?.balance_category !== 'unlimited' && (
                              <div className="leave-request-calculation-row">
                                <span className="leave-request-calc-label">Current Balance:</span>
                                <span className="leave-request-calc-value">
                                  {getSelectedLeaveType()?.balance_category === 'personal_bereavement' 
                                    ? formatDays(leaveBalance?.personal_bereavement_available || 0)
                                    : formatDays(leaveBalance?.available_days || 0)
                                  } days
                                </span>
                              </div>
                            )}
                            
                            <div className={`leave-request-balance-message ${getBalanceCheckClass()}`}>
                              {getBalanceCheckMessage()}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Additional Information & Review */}
              {currentStep === 3 && (
                <div className="leave-request-form-step">
                  <h4 className="leave-request-step-title">Additional Information</h4>
                  <div className="leave-request-additional-info">
                    <div className="leave-request-input-group">
                      <label className="leave-request-label">
                        Reason {getSelectedLeaveType()?.requires_reason && <span className="leave-request-required">*</span>}
                      </label>
                      <textarea
                        className="leave-request-textarea"
                        value={formData.reason}
                        onChange={(e) => setFormData({...formData, reason: e.target.value})}
                        placeholder="Provide reason for your leave request..."
                        rows="4"
                      />
                    </div>

                    {/* Sick Note Upload - Required for consecutive sick leave >= 3 days */}
                    {requiresSickNote && (
                      <div className="sick-note-upload-section">
                        <div className="sick-note-required-alert">
                          <i className="fas fa-info-circle" style={{marginRight: '8px'}}></i>
                          {consecutiveSickDays > calculatedDays ? (
                            <span>Sick note required: <strong>{consecutiveSickDays} consecutive days</strong> (with prior leave)</span>
                          ) : (
                            'Sick note required for 3 or more consecutive days'
                          )}
                        </div>
                        
                        {/* Radio buttons for upload mode selection */}
                        <div className="sick-note-mode-selector">
                          <label className="sick-note-radio-option">
                            <input
                              type="radio"
                              name="sickNoteMode"
                              value="upload"
                              checked={sickNoteMode === 'upload'}
                              onChange={(e) => {
                                setSickNoteMode(e.target.value);
                                setSelectedExistingSickNote(null);
                              }}
                            />
                            <span>Upload New Sick Note</span>
                          </label>
                          <label className="sick-note-radio-option">
                            <input
                              type="radio"
                              name="sickNoteMode"
                              value="select"
                              checked={sickNoteMode === 'select'}
                              onChange={(e) => {
                                setSickNoteMode(e.target.value);
                                setSickNoteFile(null);
                                setSickNoteInfo(null);
                              }}
                            />
                            <span>Select Existing Sick Note</span>
                          </label>
                        </div>

                        {/* Upload New Mode */}
                        {sickNoteMode === 'upload' && (
                          <>
                            {!sickNoteFile ? (
                              <div className="sick-note-file-input-wrapper">
                                <input
                                  type="file"
                                  id="sick-note-file-input"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={handleSickNoteFileChange}
                                  style={{display: 'none'}}
                                />
                                <button
                                  type="button"
                                  className="sick-note-upload-btn"
                                  onClick={() => document.getElementById('sick-note-file-input').click()}
                                >
                                  <i className="fas fa-file-medical" style={{marginRight: '8px'}}></i>
                                  Upload Sick Note
                                </button>
                                <span className="sick-note-file-hint">PDF, JPG, or PNG (max 10MB)</span>
                              </div>
                            ) : (
                              <div className="sick-note-info-card">
                                <div className="sick-note-info-card-header">
                                  <i className="fas fa-check-circle sick-note-info-card-icon"></i>
                                  <span className="sick-note-info-card-title">Sick Note Attached</span>
                                  <button
                                    type="button"
                                    className="sick-note-close-x"
                                    onClick={removeSickNoteFile}
                                    aria-label="Remove sick note"
                                  >
                                    ×
                                  </button>
                                </div>
                                <div className="sick-note-info-card-body">
                                  <div className="sick-note-info-row">
                                    <span className="sick-note-info-label">File:</span>
                                    <span className="sick-note-info-value">{sickNoteInfo?.name}</span>
                                  </div>
                                  <div className="sick-note-info-row">
                                    <span className="sick-note-info-label">Size:</span>
                                    <span className="sick-note-info-value">{formatFileSize(sickNoteInfo?.size || 0)}</span>
                                  </div>
                                  <div className="sick-note-info-row">
                                    <span className="sick-note-info-label">Date:</span>
                                    <span className="sick-note-info-value">
                                      {new Date(sickNoteInfo?.uploadDate).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Select Existing Mode */}
                        {sickNoteMode === 'select' && (
                          <div className="sick-note-select-wrapper">
                            {loadingSickNotes ? (
                              <div className="sick-note-loading">
                                <LoadingSpinner />
                                <span>Loading available sick notes...</span>
                              </div>
                            ) : availableSickNotes.length > 0 ? (
                              <div className="sick-note-list">
                                <label className="sick-note-list-label">Select a sick note:</label>
                                <select
                                  className="sick-note-select-dropdown"
                                  value={selectedExistingSickNote || ''}
                                  onChange={(e) => setSelectedExistingSickNote(e.target.value)}
                                >
                                  <option value="">-- Choose a sick note --</option>
                                  {availableSickNotes.map((note) => {
                                    const docId = note.Id;
                                    const fileName = note.Fields?.find(f => f.FieldName === 'SICK_NOTE_NAME')?.Item || 'Sick Note';
                                    const dateUploaded = note.Fields?.find(f => f.FieldName === 'DATE_UPLOADED')?.Item;
                                    const displayDate = dateUploaded ? new Date(dateUploaded).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    }) : 'Unknown date';
                                    
                                    return (
                                      <option key={docId} value={docId}>
                                        {fileName} - Uploaded: {displayDate}
                                      </option>
                                    );
                                  })}
                                </select>
                                {selectedExistingSickNote && (
                                  <div className="sick-note-selected-info">
                                    <i className="fas fa-check-circle" style={{color: '#28a745', marginRight: '8px'}}></i>
                                    <span>Sick note selected</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="sick-note-empty-state">
                                <i className="fas fa-folder-open" style={{fontSize: '32px', color: '#999', marginBottom: '10px'}}></i>
                                <p>No existing sick notes found.</p>
                                <p style={{fontSize: '14px', color: '#666'}}>Switch to "Upload New Sick Note" to upload one.</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Return to work information */}
                    {formData.end_date && (
                      <div className="leave-request-rtw-info">
                        <h5 className="leave-request-rtw-title">Return to Work</h5>
                        <p className="leave-request-rtw-text">You will need to confirm your return to work after your leave ends.</p>
                        <div className="leave-request-expected-return">
                          Expected return date: <strong>
                            {(() => {
                              const endDate = new Date(formData.end_date);
                              const returnDate = new Date(endDate);
                              returnDate.setDate(returnDate.getDate() + 1);
                              // Skip weekends
                              while (returnDate.getDay() === 0 || returnDate.getDay() === 6) {
                                returnDate.setDate(returnDate.getDate() + 1);
                              }
                              return formatDate(returnDate.toISOString().split('T')[0]);
                            })()}
                          </strong>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Review Summary */}
                  <h4 className="leave-request-step-title" style={{marginTop: '2rem'}}>Review Request</h4>
                  <div className="leave-request-summary">
                    <div className="leave-request-summary-item">
                      <label className="leave-request-summary-label">Leave Type:</label>
                      <span className="leave-request-leave-type-badge" style={{backgroundColor: getSelectedLeaveType()?.color}}>
                        {getSelectedLeaveType()?.name}
                      </span>
                    </div>
                    <div className="leave-request-summary-item">
                      <label className="leave-request-summary-label">Dates:</label>
                      <span className="leave-request-summary-value">{formatDate(formData.start_date)} - {formatDate(formData.end_date)}</span>
                    </div>
                    <div className="leave-request-summary-item">
                      <label className="leave-request-summary-label">Total Days:</label>
                      <span className="leave-request-summary-value">{formatDays(calculatedDays)} days</span>
                    </div>
                    {/* Public Holiday Exclusion Info */}
                    {(getSelectedLeaveType()?.balance_category === 'annual' || getSelectedLeaveType()?.balance_category === 'personal_bereavement') && (
                      <div className="leave-request-summary-item" style={{
                        backgroundColor: '#E3F2FD',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #90CAF9',
                        marginTop: '0.5rem'
                      }}>
                        <div style={{display: 'flex', alignItems: 'flex-start', gap: '0.5rem'}}>
                          <i className="fas fa-info-circle" style={{color: '#1976D2', marginTop: '0.2rem', fontSize: '14px'}}></i>
                          <span style={{color: '#0D47A1', fontSize: '0.85rem', lineHeight: '1.4'}}>
                            Weekends and public holidays are automatically excluded. Global holidays (Christmas, Boxing Day, New Year's) apply to all employees.
                            {user?.location && ` Location-specific holidays for ${user.location} are also excluded.`}
                          </span>
                        </div>
                      </div>
                    )}
                    {formData.reason && (
                      <div className="leave-request-summary-item">
                        <label className="leave-request-summary-label">Reason:</label>
                        <span className="leave-request-summary-value">{formData.reason}</span>
                      </div>
                    )}
                    {getSelectedLeaveType()?.balance_category !== 'unlimited' && (
                      <div className="leave-request-summary-item">
                        <label className="leave-request-summary-label">Balance After:</label>
                        <span className="leave-request-summary-value">
                          {formatDays(
                            (getSelectedLeaveType()?.balance_category === 'personal_bereavement' 
                              ? (leaveBalance?.personal_bereavement_available || 0)
                              : (leaveBalance?.available_days || 0)
                            ) - calculatedDays
                          )} days remaining
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="leave-request-modal-footer">
          {currentStep > 1 && (
            <button className="leave-request-btn-secondary" onClick={previousStep}>
              Previous
            </button>
          )}
          {currentStep < 3 ? (
            <button className="leave-request-btn-primary" onClick={nextStep}>
              Next
            </button>
          ) : (
            <button 
              className="leave-request-btn-primary" 
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          )}
          <button className="leave-request-btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

export default LeaveRequestModal;
